import { useAuthentication } from "services";
import axios, { AxiosResponse } from "axios";
import {
  AddImagePayload,
  GetCreateExpenseImage,
  DeleteExpenseResponse,
  ImageProfileId,
  SubmitExpensePayload,
  SubmitExpenseResponse,
  ValidateExpenseDataResponse,
  ValidationExpensePayload,
  BasicRoute,
} from "types";
import { CreateExpenseRequest } from "driverExpense/types/createExpenseRequest";
import {
  FuelPlanFeedbackRequest,
  FuelPlanSurveyResult,
  FuelPlanSurveyStatus,
} from "types/fuelPlanFeedback";

const useManagerService = () => {
  const {
    getAuthHeader,
    getAuthHeaderNoContentType,
    getUser,
    getUserClaim,
  } = useAuthentication();

  const serviceUrl = process.env.REACT_APP_MANAGER_SERVICE;

  const addImage = async (
    formData: FormData
  ): Promise<GetCreateExpenseImage | undefined> => {
    const headers = await getAuthHeaderNoContentType();
    const response: AxiosResponse<GetCreateExpenseImage> | void = await axios
      .post(`${serviceUrl}/Image/AddImage`, formData, {
        headers: { ...headers },
      })
      .catch();
    if (response) {
      const data: GetCreateExpenseImage =
        response && response.data && (await response.data);
      return data;
    }
  };

  const deleteImage = async (formData: any) => {
    const response = await axios.post(
      `${serviceUrl}/Image/DeleteImage`,
      formData,
      {
        headers: await getAuthHeader(),
      }
    );
    const { data } = await response;
    return data;
  };

  const getExpenseTypes = async () => {
    const response = await axios.get(
      `${serviceUrl}/Expense/GetExpenseTypesForDrivers`,
      {
        headers: await getAuthHeader(),
      }
    );
    const { data } = await response;
    return data;
  };

  const createExpense = async (request: CreateExpenseRequest) => {
    const response = await axios.post(
      `${serviceUrl}/Expense/createExpense`,
      request,
      {
        headers: { ...(await getAuthHeader()) },
      }
    );

    if (response) {
      const data = await response.data;
      return data;
    }
    return;
  };

  const getImage = async (
    imageProfileId: ImageProfileId
  ): Promise<string | undefined> => {
    const response = await axios.get(
      `${serviceUrl}/Image/GetImage/${imageProfileId}`,
      {
        headers: await getAuthHeader(),
      }
    );
    if (response) {
      const data: string | undefined =
        response && response.data ? response.data : undefined;
      return data;
    }
  };

  const submitExpense = async (
    formvalue: SubmitExpensePayload[],
    UUID: string
  ): Promise<SubmitExpenseResponse[] | undefined> => {
    const finalPayload = { expenses: formvalue, userId: UUID };
    const response = await axios.post(
      `${serviceUrl}/Expense/SubmitExpenses`,
      finalPayload,
      {
        headers: await getAuthHeader(),
      }
    );
    if (response) {
      const data: SubmitExpenseResponse[] =
        response && response.data && (await response.data);
      return data;
    }
  };

  const deleteExpense = async (
    formvalue: { id?: number },
    UUID: string
  ): Promise<DeleteExpenseResponse | undefined> => {
    const finalPayload = { ...formvalue, userId: UUID };
    const response = await axios.post(
      `${serviceUrl}/Expense/DeleteExpense`,
      finalPayload,
      {
        headers: { ...(await getAuthHeader()) },
      }
    );
    if (response) {
      const data: DeleteExpenseResponse =
        response && response.data && (await response.data);
      return data;
    }
  };

  const validateExpenses = async (
    formvalue: ValidationExpensePayload[]
  ): Promise<ValidateExpenseDataResponse | undefined> => {
    const finalPayload = [formvalue];
    const response = await axios.post(
      `${serviceUrl}/Expense/ValidateExpenses`,
      finalPayload,
      {
        headers: { ...(await getAuthHeader()) },
      }
    );
    if (response) {
      const data: ValidateExpenseDataResponse =
        response && response.data && (await response.data);
      return data;
    }
  };

  // TODO: need to change this to the Driver's EmployeeId
  const GetDriverProfileByUserId = async (userID: any) => {
    const response = await axios.get(
      `${serviceUrl}/Expense/GetDriverProfileByUserId/${userID}`,
      {
        headers: await getAuthHeader(),
      }
    );
    if (response && response.data) {
      const data = response.data;
      return data;
    }
    return;
  };
  const filteredExpenses = async (formvalue: any): Promise<any> => {
    const response = await axios.post(
      `${serviceUrl}/Expense/GetFilteredExpenses`,
      JSON.stringify(formvalue),
      {
        headers: { ...(await getAuthHeader()) },
      }
    );
    const data =
      response && response.data && Array.isArray(response.data)
        ? response.data
        : [];
    return data;
  };

  const getBasicRouteByLoadNumber = async (
    loadNumber: string
  ): Promise<BasicRoute | undefined> => {
    const response: AxiosResponse<BasicRoute> | void = await axios.get(
      `${serviceUrl}/Route/BasicRouteByLoadNumber?loadNumber=${loadNumber}`,
      {
        headers: { ...(await getAuthHeader()) },
      }
    );

    const data: BasicRoute | undefined =
      response && response.data ? response.data : undefined;
    if (data) data.type = "BasicRoute";
    return data;
  };

  const logFuelPlanAccess = async (loadNumber: string): Promise<void> => {
    const driverId = await getUserClaim("driverId");

    if (!driverId) {
      console.warn("Fuel plan access log skipped: missing driverId claim.");
      return;
    }

    await axios.post(
      `${serviceUrl}/FuelPlan/Access`,
      { driverId: driverId.toString(), loadNumber },
      {
        headers: { ...(await getAuthHeader()) },
      }
    );
  };

  const getFuelPlanSurveyStatus = async (
    loadNumber: string,
    generationId?: string | null
  ): Promise<FuelPlanSurveyStatus> => {
    const params = new URLSearchParams();
    params.append("loadNumber", loadNumber);
    if (generationId) params.append("generationId", generationId);

    const response = await axios.get(
      `${serviceUrl}/FuelPlan/GetFeedbackStatus?${params.toString()}`,
      { headers: { ...(await getAuthHeader()) } }
    );

    return response.data;
  };

  const submitFuelPlanSurvey = async (
    request: FuelPlanFeedbackRequest
  ): Promise<FuelPlanSurveyResult> => {
    const response = await axios.post(
      `${serviceUrl}/FuelPlan/SubmitFeedback`,
      request,
      { headers: { ...(await getAuthHeader()) } }
    );

    return response.data;
  };

  return {
    filteredExpenses: filteredExpenses,
    addImage: addImage,
    deleteImage: deleteImage,
    getExpenseTypes: getExpenseTypes,
    createExpense: createExpense,
    validateExpenses: validateExpenses,
    deleteExpense: deleteExpense,
    getImage: getImage,
    submitExpense: submitExpense,
    GetDriverProfileByUserId: GetDriverProfileByUserId,
    getBasicRouteByLoadNumber: getBasicRouteByLoadNumber,
    logFuelPlanAccess: logFuelPlanAccess,
    getFuelPlanSurveyStatus: getFuelPlanSurveyStatus,
    submitFuelPlanSurvey: submitFuelPlanSurvey,
  };
};

export default useManagerService;
